import { useCallback } from "react";
import styled from "@emotion/styled";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import type { Uuid } from "@/domains/global/identifiers";
import {
  AudioRecordingCard,
  AudioRecordingCardBody,
  AudioRecordingCardHeader,
  AudioRecordingCardLabel,
} from "@/pages/audio-recordings/shared/audio-recording-card";
import { useCapturedAudioPlayback } from "@/pages/audio-recordings/shared/use-captured-audio-playback";

export type CapturedAudioUtilitiesCardProps = {
  audioRecordingId: Uuid | null;
  onDeleteSession?: () => void;
  canDelete?: boolean;
  canPlay?: boolean;
  canStartSourceUpload?: boolean;
  canRunPipeline?: boolean;
  sourceUploadStatus?: string | null;
  pipelineStatus?: string | null;
  pipelineOutcome?: string | null;
  pipelineFailureMessage?: string | null;
  onStartSourceUpload?: () => void;
  onRunPipeline?: () => void;
  playButtonLabel?: string;
  deleteButtonLabel?: string;
  sourceUploadButtonLabel?: string;
  runPipelineButtonLabel?: string;
};

export const CapturedAudioUtilitiesCard = ({
  audioRecordingId,
  onDeleteSession,
  canDelete = true,
  canPlay = true,
  canStartSourceUpload = false,
  canRunPipeline = false,
  sourceUploadStatus,
  pipelineStatus,
  pipelineOutcome,
  pipelineFailureMessage,
  onStartSourceUpload,
  onRunPipeline,
  playButtonLabel = "Play Captured Audio",
  deleteButtonLabel = "Delete Session",
  sourceUploadButtonLabel = "Start source upload",
  runPipelineButtonLabel = "Run pipeline",
}: CapturedAudioUtilitiesCardProps) => {
  const playback = useCapturedAudioPlayback({ audioRecordingId, canPlay });

  const handlePlay = useCallback(async () => {
    await playback.play();
  }, [playback]);

  const handleAudioEnd = useCallback(() => {
    playback.clear();
  }, [playback]);

  const handleAudioError = useCallback(() => {
    playback.setError("Failed to play recorded audio.");
    playback.clear();
  }, [playback]);

  return (
    <AudioRecordingCard>
      <AudioRecordingCardHeader title="Playback & Recovery" />
      <AudioRecordingCardBody>
        <AudioRecordingCardLabel variant={MDSTextVariant.TextXs}>Actions</AudioRecordingCardLabel>
        <ButtonRow>
          <MDSButton
            variant={MDSButtonVariant.Subtle}
            size={MDSButtonSize.Small}
            onClick={() => void handlePlay()}
            isDisabled={!canPlay || playback.isPreparing}
          >
            {playback.isPreparing ? "Preparing…" : playButtonLabel}
          </MDSButton>
          {onStartSourceUpload && (
            <MDSButton
              variant={MDSButtonVariant.Emphasis}
              onClick={onStartSourceUpload}
              isDisabled={!canStartSourceUpload}
            >
              {sourceUploadButtonLabel}
            </MDSButton>
          )}
          {onRunPipeline && (
            <MDSButton
              variant={MDSButtonVariant.Emphasis}
              onClick={onRunPipeline}
              isDisabled={!canRunPipeline}
            >
              {runPipelineButtonLabel}
            </MDSButton>
          )}
          {onDeleteSession && (
            <MDSButton
              variant={MDSButtonVariant.Danger}
              onClick={onDeleteSession}
              isDisabled={!canDelete}
            >
              {deleteButtonLabel}
            </MDSButton>
          )}
        </ButtonRow>

        {(sourceUploadStatus || pipelineStatus || pipelineOutcome) && (
          <StatusSummary>
            {sourceUploadStatus && (
              <StatusItem>
                <StatusLabel variant={MDSTextVariant.TextXs}>Source upload</StatusLabel>
                <MDSText variant={MDSTextVariant.TextSm}>{sourceUploadStatus}</MDSText>
              </StatusItem>
            )}
            {pipelineStatus && (
              <StatusItem>
                <StatusLabel variant={MDSTextVariant.TextXs}>Pipeline</StatusLabel>
                <MDSText variant={MDSTextVariant.TextSm}>{pipelineStatus}</MDSText>
              </StatusItem>
            )}
            {pipelineOutcome && (
              <StatusItem>
                <StatusLabel variant={MDSTextVariant.TextXs}>Outcome</StatusLabel>
                <MDSText variant={MDSTextVariant.TextSm}>{pipelineOutcome}</MDSText>
              </StatusItem>
            )}
          </StatusSummary>
        )}

        {pipelineFailureMessage && (
          <StatusMessage tone="error" variant={MDSTextVariant.TextSm}>
            {pipelineFailureMessage}
          </StatusMessage>
        )}
        {playback.error && (
          <StatusMessage tone="error" variant={MDSTextVariant.TextSm}>
            {playback.error}
          </StatusMessage>
        )}
        <HiddenAudioElement
          controls
          ref={playback.audioElementRef}
          src={playback.playbackUrl ?? undefined}
          onEnded={handleAudioEnd}
          onError={handleAudioError}
        />
      </AudioRecordingCardBody>
    </AudioRecordingCard>
  );
};

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const StatusSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusLabel = styled(MDSText)`
  color: ${MDSTextColor.Secondary};
`;

const StatusMessage = styled(MDSText)<{ tone?: "error" }>`
  color: ${({ theme, tone }) =>
    tone === "error" ? theme.colors.text.danger : theme.colors.text.secondary};
`;

const HiddenAudioElement = styled.audio`
  display: none;
`;
